import React, { useState } from "react";

// Mirrors the backend payload from
// agent_runtime/surfaces/superdoc.py:_handle_preference_proposed_event.
export interface PreferenceProposalData {
  proposal_id: string;
  question: string;
  scope: "user" | "client" | "group";
  preference_type: string;
  key: string;
  value_preview: Record<string, unknown>;
}

interface PreferenceProposalWidgetProps {
  data: PreferenceProposalData;
  baseUrl: string;
  headers: Record<string, string>;
  // Optional callback for telemetry / refreshing a prefs panel — not
  // wired anywhere by default. Resolution itself is purely a REST call;
  // the widget doesn't need to mutate chat state.
  onResolved?: (status: "confirmed" | "dismissed") => void;
  disabled?: boolean;
}

type ResolutionState =
  | { kind: "pending" }
  | { kind: "submitting"; action: "confirm" | "dismiss" }
  | { kind: "resolved"; action: "confirm" | "dismiss" }
  | { kind: "error"; message: string };

const MemoryIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path
      d="M8 1.5C5 1.5 3 3.5 3 6.5C3 8 3.5 9 4 10L4 12.5C4 13 4.5 13.5 5 13.5L11 13.5C11.5 13.5 12 13 12 12.5L12 10C12.5 9 13 8 13 6.5C13 3.5 11 1.5 8 1.5Z"
      stroke="#6366F1"
      strokeWidth="1.3"
      fill="#EEF2FF"
    />
    <circle cx="8" cy="6.5" r="1.5" fill="#6366F1" />
  </svg>
);

const SCOPE_LABEL: Record<PreferenceProposalData["scope"], string> = {
  user: "Just for you",
  client: "Only for this patient",
  group: "Across your group",
};

const PreferenceProposalWidget: React.FC<PreferenceProposalWidgetProps> = ({
  data,
  baseUrl,
  headers,
  onResolved,
  disabled,
}) => {
  const [state, setState] = useState<ResolutionState>({ kind: "pending" });
  if (!data || !data.proposal_id || !data.question) return null;

  const submit = async (action: "confirm" | "dismiss") => {
    if (state.kind === "submitting" || state.kind === "resolved") return;
    setState({ kind: "submitting", action });
    try {
      const res = await fetch(`${baseUrl}/agent/preferences/${encodeURIComponent(data.proposal_id)}/${action}/`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(body || `HTTP ${res.status}`);
      }
      setState({ kind: "resolved", action });
      onResolved?.(action === "confirm" ? "confirmed" : "dismissed");
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    }
  };

  const isSubmitting = state.kind === "submitting";
  const isResolved = state.kind === "resolved";
  const buttonsDisabled = disabled || isSubmitting || isResolved;

  // The exact text that would be saved, so the clinician sees precisely
  // what they're agreeing to remember (not just the generic question).
  const savedInstruction =
    typeof data.value_preview?.instruction === "string" ? (data.value_preview.instruction as string) : "";

  // Resolved state — show a compact confirmation banner instead of buttons.
  if (isResolved) {
    const resolvedAction = (state as { action: "confirm" | "dismiss" }).action;
    return (
      <div style={{ ...styles.container, ...styles.containerResolved }}>
        <div style={styles.header}>
          <MemoryIcon />
          <span style={styles.headerText}>
            {resolvedAction === "confirm" ? "Saved as a preference" : "Dismissed — won't ask again for this proposal"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <MemoryIcon />
        <span style={styles.headerText}>Remember this?</span>
        <span style={styles.scopeBadge}>{SCOPE_LABEL[data.scope] || data.scope}</span>
      </div>
      <div style={styles.question}>{data.question}</div>
      {savedInstruction ? <div style={styles.savedInstruction}>“{savedInstruction}”</div> : null}
      <div style={styles.actionsRow}>
        <button
          type="button"
          disabled={buttonsDisabled}
          onClick={() => submit("confirm")}
          style={{
            ...styles.button,
            ...styles.confirmButton,
            ...(buttonsDisabled ? styles.buttonDisabled : {}),
          }}
        >
          {isSubmitting && (state as { action: string }).action === "confirm" ? "Saving…" : "Yes, remember"}
        </button>
        <button
          type="button"
          disabled={buttonsDisabled}
          onClick={() => submit("dismiss")}
          style={{
            ...styles.button,
            ...styles.dismissButton,
            ...(buttonsDisabled ? styles.buttonDisabled : {}),
          }}
        >
          {isSubmitting && (state as { action: string }).action === "dismiss" ? "Dismissing…" : "Not this time"}
        </button>
      </div>
      {state.kind === "error" ? <div style={styles.errorRow}>Couldn't save: {state.message}</div> : null}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 14px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
  },
  containerResolved: {
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerText: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#111827",
  },
  scopeBadge: {
    marginLeft: "auto",
    fontSize: "11px",
    fontWeight: 500,
    color: "#4338CA",
    backgroundColor: "#E0E7FF",
    padding: "2px 8px",
    borderRadius: "999px",
  },
  question: {
    fontSize: "14px",
    color: "#1F2937",
    lineHeight: "1.4",
  },
  savedInstruction: {
    fontSize: "13px",
    fontStyle: "italic",
    color: "#4338CA",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E0E7FF",
    borderRadius: "8px",
    padding: "6px 10px",
    lineHeight: "1.4",
  },
  actionsRow: {
    display: "flex",
    gap: "8px",
    marginTop: "4px",
  },
  button: {
    flex: "0 1 auto",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background-color 0.15s",
  },
  confirmButton: {
    backgroundColor: "#4338CA",
    color: "#FFFFFF",
    borderColor: "#4338CA",
  },
  dismissButton: {
    backgroundColor: "#FFFFFF",
    color: "#374151",
    borderColor: "#D1D5DB",
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  errorRow: {
    fontSize: "12px",
    color: "#B91C1C",
  },
};

export default PreferenceProposalWidget;
